import { Row } from '@tanstack/react-table';

import { Checkbox } from '@/components/ui/checkbox';
import i18n from '@/core/i18n';

import { isLoadMoreRow } from '../../../helpers/approval-load-more-row.helper';
import {
  isApprovalMemberRow,
  isApprovalTimeOffEntry,
} from '../../../helpers/approval-row-model.helper';
import { useApprovalsStore } from '../../../store/approvals.store';
import {
  ApprovalEntryExclusion,
  ApprovalExclusion,
  ApprovalExclusionKind,
  ApprovalMemberExclusion,
  ApprovalMemberRow,
  ApprovalProjectRow,
  ApprovalTimeEntry,
} from '../../../types/time-tracking-approvals';
import { isTimeEntryRow } from './approval-row.helpers';

interface ApprovalSelectCellProps {
  row: Row<ApprovalProjectRow>;
}

/** Nearest ancestor row whose own id is selected in the store, or null. */
const findSelectedAncestor = (
  row: Row<ApprovalProjectRow>,
  rowSelection: Record<string, boolean>
): Row<ApprovalProjectRow> | null => {
  let parent = row.getParentRow();
  while (parent) {
    if (rowSelection[parent.id]) return parent;
    parent = parent.getParentRow();
  }
  return null;
};

/**
 * Nearest EXCLUDED row on the path from `row` (inclusive) up to — but not including —
 * the selected ancestor. An exclusion anywhere on that path uncovers the row.
 */
const findExclusionOnPath = (
  row: Row<ApprovalProjectRow>,
  ancestorRowId: string,
  exclusions: Record<string, ApprovalExclusion>
): Row<ApprovalProjectRow> | null => {
  let current: Row<ApprovalProjectRow> | undefined = row;
  while (current && current.id !== ancestorRowId) {
    if (exclusions[current.id]) return current;
    current = current.getParentRow();
  }
  return null;
};

/** Loaded, selectable leaf time-entry rows under a row (excludes time-off + sentinels). */
const getSelectableLeafRows = (row: Row<ApprovalProjectRow>): Row<ApprovalProjectRow>[] =>
  row
    .getLeafRows()
    .filter(
      leaf =>
        isTimeEntryRow(leaf) &&
        !isLoadMoreRow(leaf.original) &&
        !isApprovalTimeOffEntry(leaf.original)
    );

/** Snapshot exclusion for a worklog entry unchecked under a selected ancestor. */
const buildEntryExclusion = (
  leaf: Row<ApprovalProjectRow>,
  ancestorRowId: string
): ApprovalEntryExclusion => {
  const entry = leaf.original as unknown as ApprovalTimeEntry;
  return {
    kind: ApprovalExclusionKind.Entry,
    rowId: leaf.id,
    ancestorRowId,
    parentRowId: leaf.getParentRow()?.id ?? ancestorRowId,
    entryId: entry.id,
    status: entry.status,
    approvedForInvoiceHours: entry.approvedForInvoiceHours,
    approvedForBillHours: entry.approvedForBillHours,
  };
};

/** Snapshot exclusion for a member row unchecked under a selected project. */
const buildMemberExclusion = (
  memberRow: Row<ApprovalProjectRow>,
  ancestorRowId: string
): ApprovalMemberExclusion => {
  const member = memberRow.original as unknown as ApprovalMemberRow;
  return {
    kind: ApprovalExclusionKind.Member,
    rowId: memberRow.id,
    ancestorRowId,
    userId: member.member.id,
    pendingWorklogs: member.pendingWorklogs,
    approvedWorklogs: member.approvedWorklogs,
    pendingApprovedForInvoiceHours: member.pendingApprovedForInvoiceHours,
    pendingApprovedForBillHours: member.pendingApprovedForBillHours,
    approvedForInvoiceHours: member.approvedForInvoiceHours,
    approvedForBillHours: member.approvedForBillHours,
  };
};

/**
 * Selection checkbox for any row in the approvals tree.
 *
 * - Leaf time-entry rows toggle their own selection (time-off rows get a spacer —
 *   they are informational and never selectable).
 * - Aggregate rows (project/member) toggle THEIR OWN selection — no expansion or
 *   child loading required. The approve/unapprove API accepts project/member level
 *   payloads (id + hash), so selecting the row itself is sufficient.
 * - A row rendered under a selected ancestor shows as checked (the ancestor covers it).
 *   Unchecking such a row records an EXCLUSION snapshot in the store — the ancestor
 *   stays selected (rendering indeterminate), every sibling stays checked, and the
 *   exclusion survives collapse/expand and filter changes. The API request carries
 *   the exclusions as excluded worklog/user ids.
 *
 * It derives its checked state from the selection STORE (subscribed here), NOT from
 * `row.getIsSelected()`. The cell re-renders independently of its memoized parent row (so the
 * row's avatar never flashes on a toggle), but `row.getIsSelected()` reads the table instance's
 * state, which only updates after the parent re-renders — i.e. it lags this cell's own
 * re-render. The store is the live source of truth, so we read selection straight from it.
 */

export function ApprovalSelectCell({ row }: ApprovalSelectCellProps) {
  const rowSelection = useApprovalsStore(state => state.rowSelection);
  const exclusions = useApprovalsStore(state => state.exclusions);
  const setRowSelection = useApprovalsStore(state => state.setRowSelection);
  const addExclusion = useApprovalsStore(state => state.addExclusion);
  const removeExclusions = useApprovalsStore(state => state.removeExclusions);
  const replaceExclusions = useApprovalsStore(state => state.replaceExclusions);

  if (isApprovalTimeOffEntry(row.original)) {
    return (
      <div className="flex items-center justify-center px-3 py-4">
        <div className="w-5" />
      </div>
    );
  }

  const selectedAncestor = findSelectedAncestor(row, rowSelection);
  const exclusionOnPath = selectedAncestor
    ? findExclusionOnPath(row, selectedAncestor.id, exclusions)
    : null;
  /** Covered = a selected ancestor includes this row and no exclusion uncovers it. */
  const isCovered = selectedAncestor !== null && exclusionOnPath === null;

  /** Exclusion row ids recorded directly under a given ancestor/parent row. */
  const exclusionsUnder = (predicate: (exclusion: ApprovalExclusion) => boolean): string[] =>
    Object.values(exclusions)
      .filter(predicate)
      .map(exclusion => exclusion.rowId);

  const toggleOwnSelection = (value: boolean) => {
    setRowSelection(prev => {
      const next = { ...prev };
      if (value) {
        next[row.id] = true;
      } else {
        delete next[row.id];
      }
      return next;
    });
  };

  if (isTimeEntryRow(row)) {
    const handleLeafToggle = (value: boolean) => {
      if (!selectedAncestor) {
        toggleOwnSelection(value);
        return;
      }
      if (!value) {
        // Covered leaf unchecked → punch a hole; everything else stays as-is.
        addExclusion(buildEntryExclusion(row, selectedAncestor.id));
        return;
      }
      if (exclusions[row.id]) {
        removeExclusions([row.id]);
        return;
      }
      // Uncovered because a member on the path is excluded: re-checking this one entry
      // narrows the member exclusion to per-entry exclusions of its loaded siblings.
      if (exclusionOnPath && isApprovalMemberRow(exclusionOnPath.original)) {
        const siblingExclusions = getSelectableLeafRows(exclusionOnPath)
          .filter(leaf => leaf.id !== row.id)
          .map(leaf => buildEntryExclusion(leaf, selectedAncestor.id));
        replaceExclusions([exclusionOnPath.id], siblingExclusions);
      }
    };

    return (
      <div className="flex cursor-pointer items-center justify-center px-3 py-4">
        <Checkbox
          checked={!!rowSelection[row.id] || isCovered}
          onCheckedChange={value => handleLeafToggle(!!value)}
          aria-label={i18n.t('common:selectRow')}
          onClick={e => e.stopPropagation()}
        />
      </div>
    );
  }

  // Aggregate (project/member) row.
  const isOwnSelected = !!rowSelection[row.id];
  const isSelected = isOwnSelected || isCovered;

  // Exclusions that make this row indeterminate: for a selected row, any exclusion
  // recorded under it; for a covered member, entry exclusions under it specifically.
  const exclusionsUnderRow = isOwnSelected
    ? exclusionsUnder(exclusion => exclusion.ancestorRowId === row.id)
    : isCovered
      ? exclusionsUnder(
          exclusion =>
            exclusion.kind === ApprovalExclusionKind.Entry && exclusion.parentRowId === row.id
        )
      : [];

  const hasSelectedDescendants =
    !isSelected &&
    row
      .getLeafRows()
      .some(
        leaf =>
          isTimeEntryRow(leaf) &&
          !isLoadMoreRow(leaf.original) &&
          !isApprovalTimeOffEntry(leaf.original) &&
          rowSelection[leaf.id]
      );

  const handleAggregateToggle = (value: boolean) => {
    if (isOwnSelected) {
      if (value) {
        // Indeterminate (has exclusions) re-checked → back to the full selection.
        removeExclusions(exclusionsUnder(exclusion => exclusion.ancestorRowId === row.id));
      } else {
        toggleOwnSelection(false); // store prunes this row's exclusions automatically
      }
      return;
    }
    if (selectedAncestor) {
      if (!value) {
        // Covered member unchecked → exclude it (and drop now-redundant entry
        // exclusions recorded under it — the member exclusion supersedes them).
        replaceExclusions(
          exclusionsUnder(
            exclusion =>
              exclusion.kind === ApprovalExclusionKind.Entry && exclusion.parentRowId === row.id
          ),
          [buildMemberExclusion(row, selectedAncestor.id)]
        );
      } else if (exclusions[row.id]) {
        // Excluded member re-checked → fully covered again.
        removeExclusions([row.id]);
      } else if (exclusionsUnderRow.length > 0) {
        // Covered member with entry exclusions re-checked → drop the holes.
        removeExclusions(exclusionsUnderRow);
      }
      return;
    }
    toggleOwnSelection(value);
  };

  return (
    <div className="flex cursor-pointer items-center justify-center px-3 py-4">
      <Checkbox
        checked={
          isSelected
            ? exclusionsUnderRow.length > 0
              ? 'indeterminate'
              : true
            : exclusions[row.id]
              ? false
              : hasSelectedDescendants
                ? 'indeterminate'
                : false
        }
        onCheckedChange={value => handleAggregateToggle(!!value)}
        aria-label={i18n.t('common:selectRow')}
        onClick={e => e.stopPropagation()}
      />
    </div>
  );
}
